import { Component, DOCUMENT, inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Story } from '../../model/story/story.model';
import { MatResizeablePanel } from '../mat-resizeable-panel/mat-resizeable-panel';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatCard, MatCardContent, MatCardHeader, MatCardImage } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { CarouselImagesDialog } from './carousel-images-dialog/carousel-images-dialog';
import { YouTubePlayer } from '@angular/youtube-player';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe, isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { AppStateStore } from '../../store/app-state.store';
import { StoryMarkerData } from '../../model/story/story-marker-data.interface';
import { ResponsiveService } from '../../services/responsive.service';
import { StoryDatePipe } from '../../pipes/story-date.pipe';
import { StoryService } from '../../services/story.service';
import { Platform } from '@angular/cdk/platform';
import { NavigationOptions } from '../navigation-options/navigation-options';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

@Component({
  selector: 'app-story-view',
  imports: [
    MatResizeablePanel,
    GoogleMap,
    RouterLink,
    MatChip,
    MatChipSet,
    MatCard,
    MatCardImage,
    MapMarker,
    MatCardHeader,
    MatCardContent,
    YouTubePlayer,
    MatButton,
    MatIcon,
    MatStepper,
    MatStep,
    MapInfoWindow,
    AsyncPipe,
    NgTemplateOutlet,
    NgClass,
    StoryDatePipe,
  ],
  templateUrl: './story-view.html',
  styleUrl: './story-view.scss',
})
export class StoryView implements OnInit, OnDestroy {
  @ViewChild(MatStepper) stepper!: MatStepper;
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  // Dependencies
  private readonly matDialog = inject(MatDialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly appStateStore = inject(AppStateStore);
  private readonly storyService = inject(StoryService);
  public readonly responsiveService = inject(ResponsiveService);
  private readonly document = inject(DOCUMENT);
  public readonly platform = inject(Platform);
  private readonly matBottomSheet = inject(MatBottomSheet);
  private readonly platformId = inject(PLATFORM_ID);
  // Data
  private routeParamsSubscription?: Subscription;
  public storyId = '';
  public story: Story = Story.createFromApi();
  public storyLoaded = false;
  public infoWindowStory: Story = this.story;
  public mapCenter = {lat: 51.9225, lng: 4.47917}; // Rotterdam
  /**
   * Map
   */
  public mapOptions: google.maps.MapOptions = {
    center: this.mapCenter,
    zoom: 12,
    mapTypeControl: false,
    fullscreenControl: true,
    streetViewControl: false,
    gestureHandling: "auto",
    backgroundColor: "#F5877C",
    // Styling is managed in the Google Cloud Console and applied via this Map ID.
    // When a mapId is set, any inline `styles` array is ignored by Google Maps,
    // so the styling lives in the cloud against this id.
    mapId: "4ca6a7959978df31dc19457c",
  };
  public markerList: StoryMarkerData[] = [];

  public ngOnInit(): void {
    // Update related OpenLinkedData script
    const collectionId: string | null = this.activatedRoute.snapshot.paramMap.get('collectionId');
    if (typeof collectionId === "string") {
      this.storyService.getStoryOpenLinkedDataBydCollectionId(collectionId)
        .subscribe((jsonLd: any): void => {
          // Remove old JSON-LD (browser + server-safe)
          const old = this.document.getElementById('dynamic-jsonld');
          if (old) {
            old.parentNode?.removeChild(old);
          }

          const script = this.document.createElement('script');
          script.setAttribute('type', 'application/ld+json');
          script.setAttribute('id', 'dynamic-jsonld');
          script.textContent = JSON.stringify(jsonLd);

          this.document.head.appendChild(script);
        });
    }

    this.resolveStoryIdAndLoad();
  }

  public ngOnDestroy(): void {
    this.routeParamsSubscription?.unsubscribe();
  }

  private resolveStoryIdAndLoad(): void {
    this.routeParamsSubscription = this.activatedRoute.paramMap.subscribe(paramMap => {

      if (paramMap.has('id')) {
        // URL: /story/:id
        const storyId = paramMap.get('id')!;
        this.storyId = storyId;
        this.appStateStore.loadStoryById(storyId).subscribe((story: Story): void => {
          this.updateComponentStory(story);
        });
        return;
      }

      // URL: /story/:area-name/:collectionId/:title
      const collectionId = paramMap.get('collectionId')!;
      if (collectionId !== '') {
        this.appStateStore.loadStoryByCollectionId(collectionId).subscribe((story: Story): void => {
          this.storyId = story.id;
          this.updateComponentStory(story);
        });
      }
    });
  }

  private updateComponentStory(story: Story): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.storyLoaded = true;
      return;
    }
    this.story = story;
    this.storyLoaded = true;
    this.markerList = [];

    if (isPlatformBrowser(this.platformId)) {
      // Story Marker
      this.markerList.push(
        {
          story: this.story,
          position: {
            lat: parseFloat(this.story.latitude),
            lng: parseFloat(this.story.longitude),
          },
          options: {
            title: story.title,
            icon: {
              path: 0,
              scale: 12,
              fillColor: "#f22e89",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
          },
        },
      );

      // Related story markers
      story.relatedStoryList.forEach((relatedStory: Story): void => {
        this.markerList.push(
          {
            story: relatedStory,
            position: {
              lat: parseFloat(relatedStory.latitude),
              lng: parseFloat(relatedStory.longitude),
            },
            options: {
              title: story.title,
              icon: {
                path: 0,
                scale: 10,
                fillColor: "#cef230",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
              },
            },
          },
        );
      });
      // Center map
      // setTimeout(() => this.centerMapToMarkers(), 300);
      this.centerMapToMarkers();
    }

    // this.updateRelatedOpenLinkedDataScript(this.storyId);
  }

  // private updateRelatedOpenLinkedDataScript(storyId: string): void {
    // this.storyService.getStoryOpenLinkedDataBydId(storyId).subscribe((jsonLd: any): void => {
    //   // Remove old JSON-LD if exists
    //   const old: HTMLElement | null = this.document.getElementById('dynamic-jsonld');
    //   if (old) old.remove();
    //
    //   // Insert new JSON-LD into <head>
    //   const script: HTMLScriptElement = this.document.createElement('script');
    //   script.type = 'application/ld+json';
    //   script.id = 'dynamic-jsonld';
    //   script.text = JSON.stringify(jsonLd);
    //
    //   this.document.head.appendChild(script);
    // });
  // }

  public centerMapToMarkers() {
    setTimeout(() => {
      if (google.maps.LatLngBounds) {
        // console.log('now');
        const bounds = new google.maps.LatLngBounds();
        if (!bounds) {
          return;
        }
        if (this.markerList.length > 1) {
          this.markerList.forEach((storyMarkerData: StoryMarkerData) => bounds.extend(storyMarkerData.position));
          this.map.googleMap!.fitBounds(bounds, {top: 50, bottom: 50, left: 50, right: 50});
        } else {
          this.mapCenter = {
            lat: parseFloat(this.story.latitude),
            lng: parseFloat(this.story.longitude),
          };
        }
      }
    });

  }

  public openInfoWindow(marker: MapMarker, markerData: StoryMarkerData): void {
    this.infoWindowStory = markerData.story;
    this.infoWindow.open(marker);
  }

  public closeInfoWindow(): void {
    this.infoWindow.close();
  }


  public videoIdFromUrl(url: string): string {
    const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:[?&]|$)/);
    return match ? match[1] : '';
  }

  public showStoryDetail(idToShow: number): void {
    this.stepper.selectedIndex = idToShow;
  }

  public openGalleryDialog(imageId: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.matDialog.open(CarouselImagesDialog, {
        height: '80vh',
        width: `${window.innerWidth * 0.8}px`,
        maxWidth: '100vw',
        maxHeight: '100vh',
        data: {
          carouselImages: this.story.carouselImages,
          selectedImageId: imageId,
        },
      });
    }
  }

  public openNavigationOptions(): void {
    this.matBottomSheet.open(
      NavigationOptions,
      {
        data: {lat: this.story.latitude, lng: this.story.longitude},
      });
  }

}
